import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '../lib/api.js'
import { useAuth } from '../store/authStore.js'
import { computeMyCulture, indexById } from '../lib/score-myculture.js'
import {
  CULTURES,
  REFLECTION_PROMPTS,
  REPORT_INTRO_PARAGRAPHS,
  MATRIX_DESCRIPTION_PARAGRAPHS,
  PRACTICE_INTRO,
  PRACTICE_LEAD,
  REFERENCES,
  TOC_ITEMS,
} from '../surveys/myculture-pref.js'
import { ArrowLeft, Printer, Footprints, Cog, Handshake, Sprout, Rocket, Target } from 'lucide-react'
import opLogo from '../assets/op.png'
import myCultureLogo from '../assets/myculture.jpg'

const ICONS = { Footprints, Cog, Handshake, Sprout, Rocket, Target }

// Anordnung in der 2x3-Matrix (links nach rechts: kurz/mittel/lang)
const GRID = [
  ['produktive', 'marktanpassung', 'marktgestaltung'], // Externe Ausrichtung (oben)
  ['verlaesslich', 'kooperation', 'transformation'],   // Interne Ausrichtung (unten)
]

const PERSON_FIELDS = [
  ['Vorname',                    (me) => me?.firstName],
  ['Nachname',                   (me) => me?.lastName],
  ['Höchster Bildungsabschluss', (_, d) => d?.bildungsabschluss],
  ['Fachrichtung',               (_, d) => d?.fachrichtung],
  ['Derzeitige Position',        (_, d) => d?.position],
  ['Führungsverantwortung',      (_, d) => d?.fuehrungsverantwortung],
  ['Derzeitiger Bereich der Organisation', (_, d) => d?.bereich],
  ['Derzeitige Branche',         (_, d) => d?.branche],
  ['Derzeitige Organisation',    (_, d) => d?.organisation],
]

export default function MyCultureResult() {
  const { accessId } = useParams()
  const me = useAuth(s => s.user)
  const [data, setData] = useState(null)
  const [completedAt, setCompletedAt] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.getResponse(accessId).then(r => {
      setData(r?.data || {})
      setCompletedAt(r?.completedAt || null)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [accessId])

  if (loading) return <div className="text-ink-soft">Lade Ergebnis…</div>
  if (!data) return <div className="text-rose-700">Kein Ergebnis gefunden.</div>

  const profile = computeMyCulture(data)
  const byId = indexById(profile)
  const demo = data.demographics || {}
  const dateLabel = formatDate(completedAt)

  return (
    <div className="space-y-8 max-w-5xl mx-auto print:space-y-0 print:max-w-none print:mx-0">
      {/* Action-Bar (nicht im Print) */}
      <div className="flex items-center justify-between print:hidden">
        <Link to="/" className="btn-ghost"><ArrowLeft size={18} /> Zurück zur Übersicht</Link>
        <button onClick={() => window.print()} className="btn-secondary"><Printer size={18} /> Drucken</button>
      </div>

      {/* Cover / Hero */}
      <header className="card overflow-hidden print:break-after-page">
        <div className="px-8 pt-8 pb-2 flex items-start justify-between gap-6">
          <div className="text-xs font-semibold tracking-widest text-teal-700 uppercase">WIR SEHEN. BUNT.</div>
          <img src={opLogo} alt="O+P Consult" className="h-10 w-auto" />
        </div>
        <div className="px-8 py-6 flex flex-col md:flex-row md:items-end md:justify-between gap-6 print:py-12">
          <div>
            <img src={myCultureLogo} alt="myculture" className="h-14 w-auto mb-4" />
            <div className="text-sm uppercase tracking-wider text-ink-soft">Ergebnisbericht · Version 2.1</div>
            <h1 className="text-3xl md:text-4xl font-bold mt-1 leading-tight">Analyse der bevorzugten<br />Organisationskultur</h1>
            <p className="text-lg text-ink mt-3">{[me?.firstName, me?.lastName].filter(Boolean).join(' ') || '—'}</p>
          </div>
          <div className="text-xs text-ink-soft md:text-right space-y-1">
            <p className="font-semibold text-ink">O+P Consult GmbH</p>
            <p>Hans-Thoma-Straße 98<br />68163 Mannheim</p>
            <p>
              <a href="mailto:bunt@op-consult.de" className="hover:underline">bunt@op-consult.de</a><br />
              <a href="https://www.op-consult.de" target="_blank" rel="noreferrer" className="hover:underline">www.op-consult.de</a>
            </p>
            {dateLabel && <p className="pt-2">Datum: {dateLabel}</p>}
            <p className="pt-3 text-[10px] text-ink-mute leading-snug">
              Der Prozess der berufsbezogenen Eignungsdiagnostik<br />
              ist nach DIN 33430 von DEKRA Assurance Services GmbH auditiert.
            </p>
          </div>
        </div>
        <div className="px-8 py-3 bg-teal-50/40 border-t border-teal-100/60 flex items-center justify-between text-xs text-ink-mute print:bg-transparent print:border-t-0 print:px-0 print:pt-8">
          <span>op-consult.de</span>
          <span>© O+P Consult GmbH</span>
          <span className="italic">Beratung, die bewegt.</span>
        </div>
      </header>

      {/* Inhaltsverzeichnis */}
      <section className="card p-8 print:break-before-page">
        <h2 className="text-xs uppercase tracking-widest text-teal-700 font-semibold mb-4">Inhaltsverzeichnis</h2>
        <ol className="space-y-2">
          {TOC_ITEMS.map((it, i) => (
            <li key={it.id} className="flex items-baseline gap-3">
              <span className="text-sm text-ink-mute w-5 shrink-0 text-right">{i + 1}</span>
              <a href={`#${it.id}`} className="text-ink hover:text-teal-700 hover:underline">
                {it.label}
              </a>
            </li>
          ))}
        </ol>
      </section>

      {/* 1. Einführung */}
      <Section number={1} id="einfuehrung" title="Einführung">
        <div className="space-y-3 text-ink leading-relaxed">
          {REPORT_INTRO_PARAGRAPHS.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </Section>

      {/* 2. Angaben zur Person */}
      <Section number={2} id="angaben" title="Angaben zur Person">
        <dl className="grid sm:grid-cols-[max-content_1fr] gap-x-6 gap-y-1.5 text-sm">
          {PERSON_FIELDS.map(([label, getter]) => (
            <FieldRow key={label} label={label} value={getter(me, demo)} />
          ))}
          {dateLabel && <FieldRow label="Datum" value={dateLabel} />}
        </dl>
      </Section>

      {/* 3. Grafische Darstellung der Ergebnisse */}
      <Section number={3} id="grafik" title="Grafische Darstellung der Ergebnisse">
        <CultureMatrix profile={profile} byId={byId} showHashtags showPct />
        <p className="text-xs text-ink-mute mt-3">
          <strong className="text-ink-soft">Abbildung 1</strong> Ausprägungsgrad der Kulturtypen. Die gelbe Umrandung zeigt die Kulturtypen mit der höchsten Zustimmung.
        </p>
        <h3 className="text-base font-semibold mt-8 mb-3">Allgemeine Beschreibung zur Grafik</h3>
        <div className="space-y-3 text-ink leading-relaxed">
          {MATRIX_DESCRIPTION_PARAGRAPHS.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </Section>

      {/* 4. Primärer Kulturtyp */}
      {profile.primary && profile.primary.pct > 0 && (
        <Section number={4} id="primaer" title="Ihre primär bevorzugte Organisationskultur">
          <p className="text-ink leading-relaxed mb-6">
            Der primäre Kulturtypus bezeichnet die Dimension, die in der Kulturanalyse die höchste
            Zustimmung erfahren hat. Ihnen ist es wichtig, dass der Fokus einer Organisation
            {' '}{axisLabel(profile.primary.axis)} und {timeLabel(profile.primary.time)} ausgerichtet ist.
            Ihre Wunschkultur kann somit dem Feld <strong>„{profile.primary.label}"</strong> zugeordnet werden.
          </p>
          <CultureBlock culture={profile.primary} aspectsLeadIn="Folgende Aspekte stehen hierbei insbesondere im Vordergrund:" />
          <PracticeBlock culture={profile.primary} />
        </Section>
      )}

      {/* 5. Sekundärer Kulturtyp */}
      {profile.secondary && (
        <Section number={5} id="sekundaer" title="Ihre sekundär bevorzugte Organisationskultur">
          <p className="text-ink leading-relaxed mb-6">
            Der sekundäre Kulturtypus bezeichnet die Dimension, die in der Kulturanalyse die zweithöchste
            Zustimmung erfahren hat. Ihnen ist es wichtig, dass der Fokus einer Organisation
            {' '}{axisLabel(profile.secondary.axis)} und {timeLabel(profile.secondary.time)} ausgerichtet ist.
            Ihre Wunschkultur kann somit dem Feld <strong>„{profile.secondary.label}"</strong> zugeordnet werden.
          </p>
          <CultureBlock culture={profile.secondary} aspectsLeadIn="Folgende Aspekte stehen für Sie besonders im Vordergrund:" />
          <PracticeBlock culture={profile.secondary} />
        </Section>
      )}

      {/* 6. Anregungen zur Selbstreflexion */}
      <Section number={6} id="reflexion" title="Anregungen zur Selbstreflexion">
        <ul className="space-y-3 text-ink leading-relaxed">
          {REFLECTION_PROMPTS.map((q, i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-teal-100 text-teal-700 grid place-items-center text-xs font-semibold mt-0.5">{i + 1}</span>
              <span>{q}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* 7. Überblick der Wertedimensionen */}
      <Section number={7} id="ueberblick" title="Überblick der kulturellen Wertedimensionen">
        <p className="text-ink leading-relaxed mb-5">
          Die kulturellen Wertedimensionen können zur Unterscheidung und Systematisierung übersichtlich in einer 6-Felder-Matrix (Abbildung 2) dargestellt werden.
        </p>
        <CultureMatrix profile={profile} byId={byId} variant="overview" />
        <p className="text-xs text-ink-mute mt-3 mb-8">
          <strong className="text-ink-soft">Abbildung 2</strong> Das kulturfunktionalistische Modell der organisationalen Wertedimensionen nach Müller, Kempen, Straatmann und Hattrup
        </p>
        <div className="space-y-6">
          {Object.values(CULTURES).map(c => (
            <div key={c.id} className="flex gap-5">
              <div className="w-16 h-16 shrink-0 rounded-full bg-teal-50 grid place-items-center ring-2 ring-teal-200">
                <CultureIcon name={c.icon} className="w-9 h-9 text-teal-700" />
              </div>
              <div className="flex-1">
                <div className="font-bold text-teal-700 uppercase tracking-wide text-sm mb-1">{c.label}:</div>
                <p className="text-ink leading-relaxed">{c.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* 8. Literaturhinweise */}
      <Section number={8} id="literatur" title="Literaturhinweise">
        <ul className="space-y-3 text-sm text-ink leading-relaxed">
          {REFERENCES.map((ref, i) => <li key={i}>{ref}</li>)}
        </ul>
      </Section>

    </div>
  )
}

function Section({ number, id, title, children }) {
  return (
    <section
      id={id}
      className="card p-8 scroll-mt-24 print:break-before-page first:print:break-before-auto"
    >
      <div className="flex items-baseline gap-3 mb-5">
        <span className="text-2xl font-bold text-teal-700 leading-none">{number}</span>
        <h2 className="text-xl font-bold uppercase tracking-wide text-teal-800">{title}</h2>
      </div>
      {children}
    </section>
  )
}

function FieldRow({ label, value }) {
  return (
    <>
      <dt className="text-ink-soft">{label}:</dt>
      <dd className="text-ink">{value || '—'}</dd>
    </>
  )
}

function CultureMatrix({ profile, byId, variant = 'detail', showHashtags = false, showPct = false }) {
  const topPct = profile.topPct
  const isOverview = variant === 'overview'
  return (
    <div className="space-y-2">
      <div className="text-center text-xs uppercase tracking-wider text-ink-soft">Externe Ausrichtung</div>
      <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-stretch">
        <div className="grid place-items-center text-xs uppercase tracking-wider text-ink-soft">
          <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Kurzfristige Ausrichtung</span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {GRID.flat().map(id => {
            const c = byId[id]
            return (
              <CultureCell
                key={id} culture={c}
                isTop={!!showPct && c.pct > 0 && c.pct === topPct}
                showHashtags={showHashtags}
                showPct={showPct}
                showShort={isOverview}
              />
            )
          })}
        </div>
        <div className="grid place-items-center text-xs uppercase tracking-wider text-ink-soft">
          <span style={{ writingMode: 'vertical-rl' }}>Langfristige Ausrichtung</span>
        </div>
      </div>
      <div className="text-center text-xs uppercase tracking-wider text-ink-soft">Interne Ausrichtung</div>
    </div>
  )
}

function CultureCell({ culture, isTop, showHashtags, showPct, showShort }) {
  return (
    <div className="rounded-2xl border border-teal-100 bg-white p-4 flex flex-col items-center text-center gap-3 relative">
      <div className={`w-20 h-20 rounded-full grid place-items-center
        ${isTop ? 'ring-4 ring-amber-300' : 'ring-2 ring-teal-200'}
        bg-teal-50`}>
        <CultureIcon name={culture.icon} className="w-10 h-10 text-teal-700" />
      </div>
      {showPct && (
        <div className={`absolute top-2 right-2 w-12 h-12 rounded-full grid place-items-center text-sm font-bold
          ${isTop ? 'bg-amber-300 text-amber-900' : 'bg-teal-100 text-teal-800'}`}>
          {culture.pct}%
        </div>
      )}
      <div>
        <div className="font-bold text-ink leading-snug">{culture.label}</div>
        {showHashtags && (
          <div className="text-[10px] text-ink-mute uppercase tracking-wider mt-2 flex flex-wrap justify-center gap-1">
            {culture.hashtags.map(h => <span key={h}>#{h}</span>)}
          </div>
        )}
        {showShort && <div className="text-xs text-ink-soft mt-2 leading-snug">{culture.short}</div>}
      </div>
    </div>
  )
}

function CultureBlock({ culture, aspectsLeadIn }) {
  return (
    <div className="space-y-6">
      <div className="flex items-start gap-5">
        <div className="w-24 h-24 shrink-0 rounded-full bg-teal-50 grid place-items-center ring-2 ring-teal-200">
          <CultureIcon name={culture.icon} className="w-14 h-14 text-teal-700" />
        </div>
        <div className="flex-1">
          <div className="font-bold text-teal-700 uppercase tracking-wide text-sm mb-2">{culture.label}:</div>
          <p className="text-ink leading-relaxed">{culture.description}</p>
        </div>
      </div>
      {culture.aspects && (
        <div className="border-t border-teal-100 pt-5">
          <p className="font-semibold mb-4">{aspectsLeadIn}</p>
          <div className="space-y-5">
            {culture.hashtags.map(tag => culture.aspects[tag] && (
              <div key={tag}>
                <div className="font-bold text-teal-700 mb-1.5">#{tag}</div>
                <p className="text-ink leading-relaxed text-sm">{culture.aspects[tag]}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function PracticeBlock({ culture }) {
  if (!culture.practice) return null
  return (
    <div className="border-t border-teal-100 mt-8 pt-6 space-y-5">
      <div>
        <p className="font-semibold mb-2">{PRACTICE_INTRO}</p>
        <p className="text-ink leading-relaxed text-sm">{PRACTICE_LEAD}</p>
      </div>
      <PracticeRow heading="Strategie:" body={culture.practice.strategy} />
      <PracticeRow heading="Strukturen und Prozesse:" body={culture.practice.structure} />
      <PracticeRow heading="Qualifikation und Führung:" body={culture.practice.leadership} />
    </div>
  )
}

function PracticeRow({ heading, body }) {
  return (
    <div>
      <h4 className="font-bold text-teal-700 mb-1.5">{heading}</h4>
      <p className="text-ink leading-relaxed text-sm">{body}</p>
    </div>
  )
}

function CultureIcon({ name, className }) {
  const Icon = ICONS[name] || Cog
  return <Icon className={className} strokeWidth={1.4} />
}

function axisLabel(axis) {
  return axis === 'extern' ? 'extern' : 'intern'
}

function timeLabel(time) {
  if (time === 'kurz') return 'kurzfristig'
  if (time === 'mittel') return 'mittelfristig'
  return 'langfristig'
}

function formatDate(iso) {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
